import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CircleNotch, ChatCircleDots } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/app";
  const isAgent = next === "/agent";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      navigate(isAgent ? "/agent" : "/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFA]">
      <Seo title="Logga in – Senzum AI" description="Logga in på Senzum AI för att hantera dina AI-agenter för chatt och telefon, kunskapsbas och konversationer." noindex />
      <div className="hidden lg:flex flex-col justify-between bg-[#0A0A0A] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 dark-grid-bg opacity-40" />
        <Link to="/" className="relative flex items-center gap-2.5">
          {isAgent ? (
            <>
              <div className="h-8 w-8 rounded-md bg-[#0033FF] flex items-center justify-center">
                <ChatCircleDots size={18} weight="fill" className="text-white" />
              </div>
              <span className="font-display font-extrabold text-xl">Senzum <span className="text-[#3D7BFF]">Chatt</span></span>
            </>
          ) : (
            <img src="/senzum-logo-white.png" alt="Senzum AI" className="h-8 w-auto" />
          )}
        </Link>
        <div className="relative">
          <h2 className="font-display font-bold text-4xl leading-tight tracking-tight">
            Välkommen tillbaka till ditt<br />AI-kommandocenter.
          </h2>
          <p className="mt-4 text-zinc-400 text-lg">Hantera dina chatt- och röstagenter, kunskap och konversationer.</p>
        </div>
        <div className="relative text-zinc-500 text-sm">© 2026 Senzum AI</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10">
            {isAgent ? (
              <>
                <div className="h-8 w-8 rounded-md bg-[#0033FF] flex items-center justify-center">
                  <ChatCircleDots size={18} weight="fill" className="text-white" />
                </div>
                <span className="font-display font-extrabold text-xl">Senzum <span className="text-[#3D7BFF]">Chatt</span></span>
              </>
            ) : (
              <img src="/senzum-logo.png" alt="Senzum AI" className="h-8 w-auto" />
            )}
          </Link>
          <h1 className="font-display font-bold text-3xl tracking-tight">
            {isAgent ? "Agentinloggning" : "Logga in"}
          </h1>
          <p className="mt-2 text-zinc-500">{isAgent ? "Logga in för att hantera kundchattar i realtid." : "Ange dina uppgifter för att komma till din dashboard."}</p>

          <div className="mt-4">
            {isAgent ? (
              <Link to="/login" data-testid="switch-to-admin" className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-500 hover:text-zinc-700">
                ← Vanlig inloggning
              </Link>
            ) : (
              <Link to="/login?next=/agent" data-testid="switch-to-agent" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0033FF] bg-blue-50 hover:bg-blue-100 rounded-lg px-3 py-2 transition-colors">
                <ChatCircleDots size={16} weight="fill" /> Växla till agentläge
              </Link>
            )}
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">E-post</label>
              <input data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none transition-all"
                placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Lösenord</label>
              <input data-testid="login-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none transition-all"
                placeholder="••••••••" />
            </div>
            <button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-6 py-2.5 font-semibold transition-colors disabled:opacity-60">
              {loading && <CircleNotch size={18} className="animate-spin" />} Logga in
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-zinc-500">
            Ny på Senzum?{" "}
            <Link data-testid="login-to-register" to="/register" className="font-semibold text-[#0033FF] hover:underline">
              Skapa ett konto
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
